/* ============================================================================
   NotificationBell — small bell + dropdown, mounted in the app bar's top-right
   (AppShell) for authenticated users. Fetches its own data via
   useNotifications(), so it needs no props and can also be rendered
   standalone on any page.
   ============================================================================ */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from './useNotifications';

export default function NotificationBell() {
  const { rows, unreadCount, markRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    function onClickAway(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  const recent = rows.slice(0, 8);

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        className="btn btn-sm"
        aria-label="Notifications"
        onClick={() => setOpen((o) => !o)}
        style={{ background: 'transparent', color: 'inherit', borderColor: 'rgba(255,255,255,0.24)' }}
      >
        <span aria-hidden>🔔</span>
        {unreadCount > 0 && <span className="badge-count">{unreadCount}</span>}
      </button>

      {open && (
        <div
          className="card"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 8px)',
            width: 340,
            maxHeight: 420,
            overflowY: 'auto',
            zIndex: 100,
            color: 'var(--ink)',
          }}
        >
          <div className="card-header row-between">
            <h3>Notifications</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/notifications')}>
              View all
            </button>
          </div>
          <div className="divider" />
          {recent.length === 0 ? (
            <p className="muted" style={{ padding: 12 }}>
              Nothing here yet.
            </p>
          ) : (
            <div className="stack">
              {recent.map((n) => (
                <button
                  key={n.id}
                  onClick={() => void markRead(n.id)}
                  className="btn-block"
                  style={{
                    textAlign: 'left',
                    background: 'transparent',
                    border: 'none',
                    padding: '8px 12px',
                    fontWeight: n.isRead ? 400 : 700,
                    cursor: 'pointer',
                  }}
                >
                  <div>{n.title}</div>
                  {n.body && <div className="muted meta">{n.body}</div>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
